/**
 * Cognito returns `SerializationException` ("Start of structure or map found
 * where not expected") when it can't deserialize the request body — i.e. the
 * request was corrupted or truncated in transit (e.g. a flaky mobile
 * connection), not rejected on its merits. The request never reached the auth
 * engine, so retrying once with a freshly serialized request is safe.
 */
export function isTransientSerializationError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  return (
    name === "SerializationException" ||
    (typeof message === "string" &&
      message.includes("Start of structure or map found where not expected"))
  );
}

/**
 * Runs `operation`, retrying it exactly once if it fails with a transient
 * request-serialization error. Any other failure propagates immediately.
 */
export async function retryOnTransientSerializationError<T>(
  operation: () => Promise<T>
): Promise<T> {
  try {
    return await operation();
  } catch (error: unknown) {
    if (!isTransientSerializationError(error)) {
      throw error;
    }
    return await operation();
  }
}
